import React, { useState, useEffect, useRef } from 'react'
import PageHeader from '../../PageHeader/PageHeader'
import ClientSearch from '../QuickSale/ClientSearch'
import { HitApi } from '../../Api/ApiHit'
import { updateClient, addServiceFollowup, searchServiceFollowup, updateServiceFollowup, searchService } from '../../constant/Constant'
import { getSelectedUnit, getElevateUser } from '../../storage/Storage'
import { followupStatusOptions } from '../../constant/Options'
import toast from 'react-hot-toast'
import { Search, Plus, Trash2, MessageSquare, DollarSign, Calendar, CheckCircle, Pencil } from 'lucide-react'
import AppButton from '../../components/AppButton/AppButton'
import { useNavigate, useParams } from 'react-router-dom'

function AddClientFollowup() {
    const [selectedClient, setSelectedClient] = useState(null)
    const navigate = useNavigate()
    const { id: urlId } = useParams()
    const [isEditMode, setIsEditMode] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(false)

    // Service search states
    const [showServiceDropdown, setShowServiceDropdown] = useState(false)
    const [serviceResults, setServiceResults] = useState([])
    const [serviceSearchTerm, setServiceSearchTerm] = useState('')
    const [isLoadingServices, setIsLoadingServices] = useState(false)

    // Ref for dropdown click outside
    const serviceDropdownRef = useRef(null)

    // Shared date & time (applies to all services)
    const [followupDate, setFollowupDate] = useState(new Date().toISOString().split('T')[0])
    const [followupTime, setFollowupTime] = useState(new Date().toTimeString().slice(0, 5))

    // Follow-up form state
    const [currentFollowup, setCurrentFollowup] = useState({
        service: null,
        originalPrice: '',
        negotiatedPrice: '',
        communication: ''
    })

    // All follow-ups list
    const [followups, setFollowups] = useState([])
    const [isSaving, setIsSaving] = useState(false)

    // Status state
    const [followupStatus, setFollowupStatus] = useState('active')

    useEffect(() => {
        if (urlId) {
            setIsEditMode(true)
            loadFollowupData()
        }
    }, [urlId])

    const loadFollowupData = async () => {
        setIsLoadingData(true)
        try {
            const json = {
                search: { _id: urlId }
            }
            const res = await HitApi(json, searchServiceFollowup)
            if (res?.statusCode === 200 && res?.data?.docs?.length > 0) {
                const data = res.data.docs[0]

                if (data.clientId) {
                    setSelectedClient(data.clientId)
                }

                if (data.status) {
                    setFollowupStatus(data.status)
                }

                if (data.serviceFollowup) {
                    // Set shared date/time from first followup
                    const firstItem = data.serviceFollowup[0]
                    if (firstItem?.followupDate) {
                        setFollowupDate(new Date(firstItem.followupDate).toISOString().split('T')[0])
                        setFollowupTime(new Date(firstItem.followupDate).toTimeString().slice(0, 5))
                    }

                    const mappedFollowups = data.serviceFollowup.map((item, index) => ({
                        id: item._id || Date.now() + index,
                        service: item.serviceId,
                        originalPrice: item.originalPrice,
                        negotiatedPrice: item.negotiated,
                        communication: item.communication,
                        timestamp: item.followupDate
                    }))

                    // Pre-fill form with first follow-up, rest go into the list
                    const [first, ...rest] = mappedFollowups
                    if (first) {
                        setCurrentFollowup({
                            service: first.service,
                            originalPrice: first.originalPrice,
                            negotiatedPrice: first.negotiatedPrice,
                            communication: first.communication
                        })
                    }
                    setFollowups(rest)
                }
            } else {
                toast.error('Failed to load follow-up data')
                navigate('/clientFollowup')
            }
        } catch (error) {
            console.error('Error loading follow-up data:', error)
            toast.error('Error loading follow-up data')
            navigate('/clientFollowup')
        } finally {
            setIsLoadingData(false)
        }
    }

    // Function to create wallet for client if needed
    const createWalletForClient = async (client) => {
        try {
            console.log('Creating wallet for client:', client._id)

            const updatePayload = {
                _id: client._id
            }

            const response = await HitApi(updatePayload, updateClient)

            if (response?.statusCode === 200) {
                console.log('Wallet created successfully for client:', client._id)

                return {
                    ...client,
                    walletId: {
                        _id: response.data?._id || 'generated_wallet_id',
                        balance: 0,
                        totalCredits: 0,
                        totalDebits: 0,
                        isActive: true,
                        isFrozen: false
                    }
                }
            } else {
                console.error('Failed to create wallet:', response)
                toast.error('Failed to create wallet for client')
                return client
            }
        } catch (error) {
            console.error('Error creating wallet for client:', error)
            toast.error('Error creating wallet for client')
            return client
        }
    }

    const handleClientSelect = async (client) => {
        if (!client.walletId) {
            console.log('Client does not have wallet, creating one...')
            toast.loading('Setting up wallet for client...', { id: 'wallet-creation' })

            const updatedClient = await createWalletForClient(client)
            toast.dismiss('wallet-creation')

            if (updatedClient.walletId) {
                toast.success('Wallet created for client')
            }

            setSelectedClient(updatedClient)
        } else {
            console.log('Client already has wallet:', client.walletId)
            setSelectedClient(client)
        }
    }

    const handleClearClient = () => {
        setSelectedClient(null)
        setFollowups([])
        resetCurrentFollowup()
        setFollowupStatus('active')
        setFollowupDate(new Date().toISOString().split('T')[0])
        setFollowupTime(new Date().toTimeString().slice(0, 5))
    }

    // Fetch services when search term changes
    useEffect(() => {
        if (showServiceDropdown && serviceSearchTerm.trim()) {
            fetchServices()
        } else {
            setServiceResults([])
        }
    }, [showServiceDropdown, serviceSearchTerm])

    // Click outside handler to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(event.target)) {
                setShowServiceDropdown(false)
            }
        }

        if (showServiceDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showServiceDropdown])

    const fetchServices = async () => {
        setIsLoadingServices(true)
        try {
            const json = {
                page: 1,
                limit: 20,
                search: {
                    name: { $regex: serviceSearchTerm, $options: 'i' }
                }
            }

            const res = await HitApi(json, searchService)
            if (res?.statusCode === 200) {
                setServiceResults(Array.isArray(res?.data?.docs) ? res.data.docs : [])
            } else {
                setServiceResults([])
            }
        } catch (error) {
            console.error('Error fetching services:', error)
            setServiceResults([])
        } finally {
            setIsLoadingServices(false)
        }
    }

    const handleServiceSelect = (service) => {
        setCurrentFollowup({
            ...currentFollowup,
            service: service,
            originalPrice: service.price || '',
            negotiatedPrice: service.price || ''
        })
        setShowServiceDropdown(false)
        setServiceSearchTerm('')
        setServiceResults([])
    }

    const resetCurrentFollowup = () => {
        setCurrentFollowup({
            service: null,
            originalPrice: '',
            negotiatedPrice: '',
            communication: ''
        })
    }

    const handleAddFollowup = () => {
        // Validation
        if (!currentFollowup.service) {
            toast.error('Please select a service')
            return
        }

        // Add to followups list with shared date/time
        const newFollowup = {
            ...currentFollowup,
            id: Date.now(),
            date: followupDate,
            time: followupTime,
            timestamp: new Date().toISOString()
        }

        setFollowups([...followups, newFollowup])
        toast.success('Follow-up added!')
        resetCurrentFollowup()
    }

    const handleRemoveFollowup = (id) => {
        setFollowups(followups.filter(f => f.id !== id))
        toast.success('Follow-up removed')
    }

    const handleEditFollowup = (followup) => {
        setCurrentFollowup({
            service: followup.service,
            originalPrice: followup.originalPrice,
            negotiatedPrice: followup.negotiatedPrice,
            communication: followup.communication
        })
        setFollowups(followups.filter(f => f.id !== followup.id))
    }

    const handleSaveAllFollowups = async () => {
        if (!selectedClient) {
            toast.error('Please select a client first')
            return
        }

        if (followups.length === 0) {
            toast.error('Please add at least one follow-up')
            return
        }

        setIsSaving(true)
        try {
            const unit = getSelectedUnit()
            const currentUser = getElevateUser()

            // Prepare serviceFollowup array
            const serviceFollowup = followups.map((followup) => {
                const originalPrice = parseFloat(followup.originalPrice) || 0
                const negotiatedPrice = parseFloat(followup.negotiatedPrice) || 0
                const discount = negotiatedPrice - originalPrice // Negative value for discount

                return {
                    serviceId: followup.service?._id || null,
                    originalPrice: followup.originalPrice.toString(),
                    negotiated: followup.negotiatedPrice.toString(),
                    followupDate: new Date(`${followupDate}T${followupTime}`).getTime(),
                    discount: discount.toString(),
                    communication: followup.communication
                }
            })

            // Create payload as per structure
            const payload = {
                clientId: selectedClient._id,
                followupBy: currentUser?._id || selectedClient._id, // Current user ID
                unitId: unit?._id || '',
                status: followupStatus, // Dynamic status from dropdown
                serviceFollowup: serviceFollowup
            }

            if (isEditMode) {
                payload._id = urlId
            }

            console.log('Saving client follow-up:', payload)

            const apiCall = isEditMode ? updateServiceFollowup : addServiceFollowup
            const response = await HitApi(payload, apiCall)

            if (response?.statusCode === 201 || response?.statusCode === 200) {
                toast.success(`${followups.length} follow-up(s) ${isEditMode ? 'updated' : 'saved'} successfully!`)
                // Reset form
                setFollowups([])
                resetCurrentFollowup()
                setFollowupStatus('active')
                setFollowupDate(new Date().toISOString().split('T')[0])
                setFollowupTime(new Date().toTimeString().slice(0, 5))
                // Optionally clear client
                // setSelectedClient(null)
                navigate("/clientFollowup")
            } else {
                toast.error(response?.message || `Failed to ${isEditMode ? 'update' : 'save'} follow-ups`)
            }

        } catch (error) {
            console.error('Error saving follow-ups:', error)
            toast.error(`Failed to ${isEditMode ? 'update' : 'save'} follow-ups`)
        } finally {
            setIsSaving(false)
        }
    }

    const calculateDiscount = () => {
        const original = parseFloat(currentFollowup.originalPrice) || 0
        const negotiated = parseFloat(currentFollowup.negotiatedPrice) || 0
        const discount = original - negotiated
        const percentage = original > 0 ? ((discount / original) * 100).toFixed(1) : 0
        return { amount: discount, percentage }
    }

    if (isLoadingData) {
        return (
            <div className='md:p-5'>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
            </div>
        )
    }

    return (
        <div className='md:p-5'>
            <PageHeader
                title={isEditMode ? "Edit Client Follow-Up" : "Client Follow-Up"}
                description={isEditMode ? "Update communications and negotiations" : "Track communications and negotiations"}
            />

            <div className="mt-2 md:mt-3 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3">
                {/* Left Column - Client & Form */}
                <div className="md:col-span-5 space-y-2 md:space-y-3">
                    {/* Client Search */}
                    <div className="bg-white p-2 md:p-3 border rounded-lg">
                        <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2">Select Client</h3>
                        <ClientSearch
                            onClientSelect={handleClientSelect}
                            selectedClient={selectedClient}
                            onClear={handleClearClient}
                        />
                        {selectedClient && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                <div className="text-xs space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-blue-600">Name:</span>
                                        <span className="font-medium text-blue-900">{selectedClient.name}</span>
                                    </div>
                                    {selectedClient.phoneNumber && (
                                        <div className="flex justify-between">
                                            <span className="text-blue-600">Phone:</span>
                                            <span className="font-medium text-blue-900">{selectedClient.phoneNumber}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status Dropdown & Shared Date/Time */}
                    {selectedClient && (
                        <div className="bg-white p-2 md:p-3 border rounded-lg space-y-2">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">Follow-Up Status</label>
                                <select
                                    value={followupStatus}
                                    onChange={(e) => setFollowupStatus(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                >
                                    {followupStatusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={followupDate}
                                        onChange={(e) => setFollowupDate(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                                    <input
                                        type="time"
                                        value={followupTime}
                                        onChange={(e) => setFollowupTime(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Follow-up Form */}
                    {selectedClient && (
                        <div className="bg-white p-2 md:p-3 border rounded-lg">
                            <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2">Add Follow-Up</h3>

                            {/* Service Search */}
                            <div className="relative mb-2" ref={serviceDropdownRef}>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search service..."
                                        value={currentFollowup.service ? currentFollowup.service.name : serviceSearchTerm}
                                        onChange={(e) => {
                                            if (!currentFollowup.service) {
                                                setServiceSearchTerm(e.target.value)
                                            }
                                        }}
                                        onFocus={() => !currentFollowup.service && setShowServiceDropdown(true)}
                                        className="w-full px-3 py-2 pr-8 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                    />
                                    <Search className="absolute right-2 top-2.5 h-4 w-4 text-gray-400" />
                                    {currentFollowup.service && (
                                        <button
                                            onClick={() => {
                                                setCurrentFollowup({ ...currentFollowup, service: null, originalPrice: '' })
                                                setServiceSearchTerm('')
                                            }}
                                            className="absolute right-8 top-2.5 text-red-500"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Service Dropdown */}
                                {showServiceDropdown && !currentFollowup.service && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 md:max-h-72 overflow-y-auto">
                                        {isLoadingServices ? (
                                            <div className="p-3 text-center">
                                                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                                            </div>
                                        ) : serviceResults.length > 0 ? (
                                            serviceResults.map((service) => (
                                                <button
                                                    key={service._id}
                                                    onClick={() => handleServiceSelect(service)}
                                                    className="w-full p-2.5 md:p-2 text-left hover:bg-blue-50 active:bg-blue-100 border-b touch-manipulation"
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm">{service.name}</span>
                                                        <span className="text-xs font-semibold text-blue-600 ml-2">
                                                            ₹{service.price || 0}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="p-3 text-xs text-center text-gray-500">
                                                {serviceSearchTerm ? 'No services found' : 'Type to search'}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Price & Date Grid */}
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Original ₹</label>
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={currentFollowup.originalPrice}
                                        onChange={(e) => setCurrentFollowup({ ...currentFollowup, originalPrice: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                        inputMode="numeric"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Negotiated ₹</label>
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={currentFollowup.negotiatedPrice}
                                        onChange={(e) => setCurrentFollowup({ ...currentFollowup, negotiatedPrice: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 touch-manipulation"
                                        inputMode="numeric"
                                    />
                                </div>
                            </div>

                            {/* Discount Display - Compact */}
                            {currentFollowup.originalPrice && currentFollowup.negotiatedPrice && (
                                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded text-center">
                                    <span className="text-xs text-green-600">Discount: </span>
                                    <span className="text-sm font-bold text-green-700">
                                        ₹{calculateDiscount().amount} ({calculateDiscount().percentage}%)
                                    </span>
                                </div>
                            )}

                            {/* Communication */}
                            <div className="mb-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Communication</label>
                                <textarea
                                    placeholder="Enter details..."
                                    value={currentFollowup.communication}
                                    onChange={(e) => setCurrentFollowup({ ...currentFollowup, communication: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500 resize-none touch-manipulation"
                                />
                            </div>

                            {/* Add Button */}
                            <button
                                onClick={handleAddFollowup}
                                className="w-full py-2.5 md:py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 active:bg-blue-800 flex items-center justify-center gap-1 touch-manipulation"
                            >
                                <Plus className="h-4 w-4" />
                                Add to List
                            </button>
                        </div>
                    )}
                </div>

                {/* Right Column - Follow-ups List */}
                <div className="md:col-span-7">
                    {followups.length > 0 ? (
                        <div className="bg-white p-2 md:p-3 border rounded-lg">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-2">
                                <h3 className="text-xs md:text-sm font-semibold text-gray-700">Follow-Ups ({followups.length})</h3>
                                {/* <button
                                    onClick={handleSaveAllFollowups}
                                    disabled={isSaving}
                                    className="w-full sm:w-auto px-4 py-2 md:py-1.5  text-white text-sm rounded bg-black flex items-center justify-center gap-1 disabled:opacity-50 touch-manipulation"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="h-4 w-4" />
                                            Save All
                                        </>
                                    )}
                                </button> */}
                                <AppButton onClick={handleSaveAllFollowups} disabled={isSaving} buttontext={isSaving ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update All' : 'Save All')} />
                            </div>

                            <div className="space-y-2 max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-200px)] overflow-y-auto">
                                {followups.map((followup, index) => (
                                    <div key={followup.id} className="p-2 border rounded bg-gray-50">
                                        <div className="flex gap-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                                                        #{index + 1}
                                                    </span>
                                                    <span className="text-xs md:text-sm font-semibold text-gray-900 line-clamp-1">{followup.service?.name}</span>
                                                </div>

                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-1 text-xs">
                                                    <div>
                                                        <span className="text-gray-500">Original:</span>
                                                        <p className="font-medium">₹{followup.originalPrice}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Negotiated:</span>
                                                        <p className="font-medium text-green-600">₹{followup.negotiatedPrice}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Date:</span>
                                                        <p className="font-medium">{followup.date}</p>
                                                    </div>
                                                    <div className="col-span-2 sm:col-span-1">
                                                        <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                                            -₹{(parseFloat(followup.originalPrice) - parseFloat(followup.negotiatedPrice)).toFixed(0)}
                                                        </span>
                                                    </div>
                                                </div>

                                                <p className="text-xs text-gray-600 line-clamp-2">{followup.communication}</p>
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <button
                                                    onClick={() => handleEditFollowup(followup)}
                                                    className="p-1.5 md:p-1 text-blue-500 hover:bg-blue-50 active:bg-blue-100 rounded h-fit touch-manipulation"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleRemoveFollowup(followup.id)}
                                                    className="p-1.5 md:p-1 text-red-500 hover:bg-red-50 active:bg-red-100 rounded h-fit touch-manipulation"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white p-6 md:p-8 border rounded-lg text-center">
                            <MessageSquare className="h-10 w-10 md:h-12 md:w-12 text-gray-300 mx-auto mb-2 md:mb-3" />
                            <p className="text-xs md:text-sm text-gray-500">No follow-ups added yet</p>
                            <p className="text-xs text-gray-400 mt-1">Select a client and add follow-up details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AddClientFollowup